import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Activity, Calendar, FileText, Leaf, MessageCircle, Newspaper, Search as SearchIcon,
  Users as UsersIcon,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useGlobalSearch, type SearchHit, type SearchKind } from "@/hooks/useSearch";
import { cn } from "@/lib/utils";

/** /search?q=… — full results page, grouped by kind. */
export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") ?? "";
  const [draft, setDraft] = useState(initialQ);
  const navigate = useNavigate();

  // Keep the URL query in sync as the user types, debounced lightly via
  // the React Query staleTime — typing fast won't fire 10 requests, the
  // useGlobalSearch hook will only invalidate on terminal keystrokes.
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (draft.trim()) next.set("q", draft.trim());
      else              next.delete("q");
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const q = params.get("q") ?? "";
  const search = useGlobalSearch(q, { limit: 12 });

  // Group results by kind so the page reads like sections.
  const buckets = bucketResults(search.data?.results ?? []);

  function onPickHit(hit: SearchHit) {
    navigate(hit.link);
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
          <SearchIcon className="h-3.5 w-3.5" />Search
        </div>
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search documents, articles, threads, indicators, projects, people…"
            className="h-12 pl-10 text-base"
          />
        </div>
        {q && (
          <p className="text-xs text-muted-foreground">
            Showing results for <span className="font-medium text-foreground">"{q}"</span>
            {search.isFetching && " · searching…"}
          </p>
        )}
      </header>

      {q.length < 2 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <p className="text-sm font-medium">Type at least two characters to search.</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Spans documents, articles, forum threads, indicators, climate projects, and people.
            </p>
          </CardContent>
        </Card>
      ) : search.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      ) : !buckets.length ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <p className="text-sm font-medium">No matches.</p>
            <p className="mt-1 text-xs text-muted-foreground">Try a different word or shorter query.</p>
          </CardContent>
        </Card>
      ) : (
        buckets.map(([kind, hits]) => (
          <section key={kind}>
            <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <KindIcon kind={kind} />
              {KIND_LABEL[kind]}
              <span className="rounded-full bg-muted px-1.5 py-px font-mono text-[10px] tabular-nums">{hits.length}</span>
            </h2>
            <Card className="border-border/60 shadow-soft">
              <CardContent className="p-0">
                <ul className="divide-y divide-border/60">
                  {hits.map((h) => (
                    <li key={`${h.kind}-${h.id}`}>
                      <button
                        type="button"
                        onClick={() => onPickHit(h)}
                        className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-accent/40"
                      >
                        <KindIcon kind={h.kind} className="mt-0.5 h-4 w-4 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{h.title}</div>
                          {h.snippet && (
                            <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                              {h.snippet}
                            </div>
                          )}
                        </div>
                        <Badge variant="outline" className="shrink-0 text-[10px] font-medium uppercase tracking-wider">
                          {KIND_LABEL[h.kind]}
                        </Badge>
                      </button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </section>
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Topbar-friendly compact result list — used by the global search popover.
// Exported so the topbar can drop it inline.
// ---------------------------------------------------------------------------
export function SearchResultsList({
  hits, onPick, loading,
}: {
  hits: SearchHit[];
  onPick: (h: SearchHit) => void;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-1 p-1.5">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
      </div>
    );
  }
  if (!hits.length) {
    return <div className="px-3 py-4 text-xs text-muted-foreground">No matches.</div>;
  }
  const buckets = bucketResults(hits);
  return (
    <div className="max-h-96 overflow-y-auto">
      {buckets.map(([kind, group]) => (
        <div key={kind}>
          <div className="border-b border-border/40 bg-muted/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {KIND_LABEL[kind]}
          </div>
          <ul>
            {group.map((h) => (
              <li key={`${h.kind}-${h.id}`}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onPick(h)}
                  className="flex w-full items-start gap-2 px-3 py-2 text-left transition hover:bg-accent/40"
                >
                  <KindIcon kind={h.kind} className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{h.title}</div>
                    {h.snippet && (
                      <div className="truncate text-[11px] text-muted-foreground">{h.snippet}</div>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
const KIND_LABEL: Record<SearchKind, string> = {
  document:  "Documents",
  article:   "Articles",
  thread:    "Forum threads",
  indicator: "Indicators",
  project:   "Climate projects",
  person:    "People",
};

function bucketResults(hits: SearchHit[]): [SearchKind, SearchHit[]][] {
  // Preserve the kind order from the backend: documents, articles,
  // threads, indicators, projects, people.
  const order: SearchKind[] = ["document", "article", "thread", "indicator", "project", "person"];
  const map = new Map<SearchKind, SearchHit[]>();
  for (const h of hits) {
    const arr = map.get(h.kind) ?? [];
    arr.push(h);
    map.set(h.kind, arr);
  }
  return order
    .filter((k) => map.has(k))
    .map((k) => [k, map.get(k)!] as [SearchKind, SearchHit[]]);
}

function KindIcon({ kind, className }: { kind: SearchKind; className?: string }) {
  const cls = cn("h-3.5 w-3.5", className);
  switch (kind) {
    case "document":  return <FileText className={cls} />;
    case "article":   return <Newspaper className={cls} />;
    case "thread":    return <MessageCircle className={cls} />;
    case "indicator": return <Activity className={cls} />;
    case "project":   return <Leaf className={cls} />;
    case "person":    return <UsersIcon className={cls} />;
    default:          return <Calendar className={cls} />;
  }
}
